import React, { useState, useEffect, useCallback } from 'react';
import {
    AcademicCapIcon,
    MagnifyingGlassIcon,
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    ClipboardDocumentListIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { CBC_LEVELS, getLevelData } from '../../utils/cbcUtils';
import BlueprintCreator from './BlueprintCreator';
import ConfirmModal from '../common/ConfirmModal';

const TemplateLibrary = ({ courseId, course, subTab, onSubTabChange, isHOS = false }) => {
    const { get, del } = useApi();
    const { showToast } = useAppState();

    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showCreator, setShowCreator] = useState(false);
    const [editingRubric, setEditingRubric] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, rubricId: null, rubricName: '' });
    const [isDeleting, setIsDeleting] = useState(false);

    // Track active band view per rubric card (default: 'INTENT')
    const [activeBands, setActiveBands] = useState({});

    const fetchTemplates = useCallback(async () => {
        try {
            setLoading(true);
            let url = '/api/cbc/templates/';
            if (courseId) {
                url += `?learning_area=${courseId}`;
            }
            const data = await get(url);
            setTemplates(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching rubrics:', error);
            showToast('Failed to load assessment rubrics', 'error');
        } finally {
            setLoading(false);
        }
    }, [get, courseId, showToast]);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const handleDeleteRubric = async () => {
        if (!deleteModal.rubricId) return;
        try {
            setIsDeleting(true);
            await del(`/api/cbc/templates/${deleteModal.rubricId}/`);
            showToast('Assessment rubric deleted successfully', 'success');
            setDeleteModal({ isOpen: false, rubricId: null, rubricName: '' });
            fetchTemplates();
        } catch (error) {
            console.error('Error deleting rubric:', error);
            showToast('Failed to delete rubric', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleBandClick = (rubricId, bandCode) => {
        setActiveBands(prev => {
            const current = prev[rubricId] || 'INTENT';
            // Toggle back to INTENT if clicking the currently active band
            return {
                ...prev,
                [rubricId]: current === bandCode ? 'INTENT' : bandCode
            };
        });
    };

    const filteredTemplates = templates.filter(t => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (t.name && t.name.toLowerCase().includes(q)) ||
            (t.description && t.description.toLowerCase().includes(q)) ||
            (t.criteria_ee && t.criteria_ee.toLowerCase().includes(q)) ||
            (t.criteria_me && t.criteria_me.toLowerCase().includes(q))
        );
    });

    const getDisplayText = (rubric, currentBand) => {
        switch (currentBand) {
            case 'EE':
                return {
                    label: 'Exceeding Expectations (EE)',
                    text: rubric.criteria_ee || 'No specific criteria defined for Exceeding Expectations.'
                };
            case 'ME':
                return {
                    label: 'Meeting Expectations (ME)',
                    text: rubric.criteria_me || 'No specific criteria defined for Meeting Expectations.'
                };
            case 'AE':
                return {
                    label: 'Approaching Expectations (AE)',
                    text: rubric.criteria_ae || 'No specific criteria defined for Approaching Expectations.'
                };
            case 'BE':
                return {
                    label: 'Below Expectations (BE)',
                    text: rubric.criteria_be || 'No specific criteria defined for Below Expectations.'
                };
            default:
                return {
                    label: 'Strategic Intent',
                    text: rubric.description || 'No strategic intent specified.'
                };
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-300">
            {/* Top Header matching LessonPlansTab exactly */}
            <div className="flex flex-col md:flex-row md:items-center justify-between bg-white rounded-[1.5rem] px-6 py-5 border border-slate-100 shadow-sm gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-indigo-50 rounded-xl text-indigo-600">
                        <AcademicCapIcon className="w-6 h-6" />
                    </div>
                    <div>
                        <h2 className="text-lg font-black text-[#18216D] uppercase tracking-tight leading-tight">
                            Assessment Rubrics
                        </h2>
                        <p className="text-slate-500 font-medium text-xs mt-1">
                            Manage and edit assessment rubrics for this learning area.
                        </p>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                    {/* Search Input */}
                    <div className="relative group w-full sm:w-64">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors">
                            <MagnifyingGlassIcon className="w-4 h-4" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search rubrics..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-[#18216D]/10 focus:border-[#18216D] transition-all text-xs font-bold text-[#18216D]"
                        />
                    </div>

                    {/* Create Rubric Button */}
                    <button
                        onClick={() => { setEditingRubric(null); setShowCreator(true); }}
                        className="px-6 py-3 bg-[#18216D] text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-[#0D164F] transition-all shadow-xl shadow-indigo-900/10 flex items-center justify-center space-x-2 whitespace-nowrap active:scale-95 shrink-0"
                    >
                        <PlusIcon className="w-4 h-4 stroke-[3]" />
                        <span>Create Rubric</span>
                    </button>
                </div>
            </div>

            {/* Shared Sub-Tab Toggle Buttons - Centered */}
            {!isHOS && onSubTabChange && (
                <div className="flex justify-center border-b border-gray-200 mb-6">
                    <nav className="flex space-x-8" aria-label="Sub-tabs">
                        <button
                            type="button"
                            onClick={() => onSubTabChange('lesson-plans')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${'lesson-plans' === subTab ? 'text-[#18216D] border-[#18216D]' : 'text-slate-400 hover:text-slate-600 border-transparent'}`}
                        >
                            <ClipboardDocumentListIcon className={`w-4 h-4 ${'lesson-plans' === subTab ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Lesson Plans</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => onSubTabChange('records')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${'records' === subTab ? 'text-[#18216D] border-[#18216D]' : 'text-slate-400 hover:text-slate-600 border-transparent'}`}
                        >
                            <DocumentTextIcon className={`w-4 h-4 ${'records' === subTab ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Records of Work</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => onSubTabChange('rubrics')}
                            className={`pb-3 px-2 text-xs font-black uppercase tracking-widest transition-all relative flex items-center gap-2 border-b-2 ${'rubrics' === subTab ? 'text-[#18216D] border-[#18216D]' : 'text-slate-400 hover:text-slate-600 border-transparent'}`}
                        >
                            <AcademicCapIcon className={`w-4 h-4 ${'rubrics' === subTab ? 'text-[#18216D]' : 'text-slate-400'}`} />
                            <span>Assessment Rubrics</span>
                        </button>
                    </nav>
                </div>
            )}

            {/* Rubrics Grid */}
            {loading ? (
                <div className="py-24 text-center">
                    <div className="w-8 h-8 border-4 border-[#18216D]/20 border-t-[#18216D] rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Loading Rubrics...</p>
                </div>
            ) : filteredTemplates.length === 0 ? (
                <div className="py-20 text-center bg-white rounded-[2.5rem] border-2 border-dashed border-slate-100 p-8">
                    <div className="w-16 h-16 bg-indigo-50 text-[#18216D] rounded-2xl flex items-center justify-center mx-auto mb-4 border border-indigo-100">
                        <AcademicCapIcon className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-black text-[#18216D]">No Assessment Rubrics Found</h3>
                    <p className="text-slate-400 text-sm font-medium mt-1 max-w-sm mx-auto">
                        {search ? 'No rubrics match your search criteria.' : 'No rubric templates have been created for this learning area yet.'}
                    </p>
                    <button
                        onClick={() => { setEditingRubric(null); setShowCreator(true); }}
                        className="mt-6 px-6 py-3 bg-[#18216D] text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-[#0D164F] transition-all shadow-lg"
                    >
                        Create First Rubric
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTemplates.map((rubric) => {
                        const currentBand = activeBands[rubric.id] || 'INTENT';
                        const displayContent = getDisplayText(rubric, currentBand);

                        return (
                            <div
                                key={rubric.id}
                                className="bg-white rounded-[2rem] border border-slate-100 p-6 hover:shadow-xl hover:shadow-indigo-900/5 transition-all group flex flex-col justify-between relative overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 w-28 h-28 bg-amber-50/40 rounded-bl-full -mr-12 -mt-12 transition-transform group-hover:scale-125"></div>

                                <div>
                                    {/* Top Bar: Clean Badge Only (Icon removed as requested) */}
                                    <div className="flex items-center justify-between mb-4 relative z-10">
                                        <span className="px-3 py-1 bg-amber-50 text-amber-700 text-[9px] font-black uppercase tracking-widest rounded-full border border-amber-100">
                                            Assessment Rubric
                                        </span>
                                    </div>

                                    {/* Rubric Title */}
                                    <h3 className="text-xl font-black text-[#18216D] tracking-tight mb-4">
                                        {rubric.name}
                                    </h3>

                                    {/* Displayed Text Label & Body (Unboxed, sitting directly on card) */}
                                    <div className="mb-6 space-y-1 min-h-[90px]">
                                        {currentBand !== 'INTENT' && (
                                            <p className={`text-[10px] font-black uppercase tracking-widest ${getLevelData(currentBand)?.color || 'text-[#18216D]'}`}>
                                                {displayContent.label}
                                            </p>
                                        )}
                                        <p className="text-slate-600 font-medium text-sm leading-relaxed italic">
                                            {displayContent.text}
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    {/* Full-Width Grade Bands (EE, ME, AE, BE) using cbcUtils.js centralized colors */}
                                    <div className="grid grid-cols-4 gap-1.5 w-full my-4">
                                        {CBC_LEVELS.map((level) => {
                                            const isActive = currentBand === level.code;
                                            const levelInfo = getLevelData(level.code);

                                            return (
                                                <button
                                                    key={level.code}
                                                    type="button"
                                                    onClick={() => handleBandClick(rubric.id, level.code)}
                                                    className={`py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all text-center border ${
                                                        isActive
                                                            ? `${levelInfo.bg || 'bg-[#18216D]'} ${levelInfo.color || 'text-[#18216D]'} ${levelInfo.border || 'border-[#18216D]'} shadow-sm font-bold scale-[1.02]`
                                                            : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100 hover:text-[#18216D]'
                                                    }`}
                                                    title={level.label}
                                                >
                                                    {level.code}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Bottom Actions Bar (Below Grade Bands) */}
                                    <div className="pt-4 border-t border-slate-100 flex items-center gap-3">
                                        <button
                                            onClick={() => { setEditingRubric(rubric); setShowCreator(true); }}
                                            className="flex-1 py-3 bg-[#18216D]/5 hover:bg-[#18216D] text-[#18216D] hover:text-white rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2 uppercase tracking-widest active:scale-95"
                                        >
                                            <PencilSquareIcon className="w-4 h-4 stroke-[2.5]" />
                                            <span>Edit Rubric</span>
                                        </button>
                                        <button
                                            onClick={() => setDeleteModal({ isOpen: true, rubricId: rubric.id, rubricName: rubric.name })}
                                            className="p-3 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all border border-slate-100 hover:border-rose-100 shrink-0"
                                            title="Delete Rubric"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Creator / Edit Modal */}
            {showCreator && (
                <BlueprintCreator
                    assignmentContext={{
                        learning_area: courseId,
                        learning_area_name: course?.name || course?.learning_area_name || (templates.length > 0 ? templates[0].learning_area_name : ''),
                        grade_level_id: course?.grade_level_id || course?.grade_level?.id || course?.grade_level || (templates.length > 0 ? templates[0].grade_level : null),
                        grade_level_name: course?.grade_level_name || course?.grade_level?.name || (templates.length > 0 ? templates[0].grade_level_name : '')
                    }}
                    initialData={editingRubric}
                    initialType="assessment"
                    onSuccess={() => {
                        setShowCreator(false);
                        setEditingRubric(null);
                        fetchTemplates();
                    }}
                    onCancel={() => {
                        setShowCreator(false);
                        setEditingRubric(null);
                    }}
                />
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, rubricId: null, rubricName: '' })}
                onConfirm={handleDeleteRubric}
                loading={isDeleting}
                title="Delete Assessment Rubric"
                message={`Are you sure you want to delete "${deleteModal.rubricName}"? This action cannot be undone.`}
                confirmText="Delete Rubric"
            />
        </div>
    );
};

export default TemplateLibrary;
